import {
  Autocomplete,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FC,
  type SyntheticEvent,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import useEvent from "react-use-event-hook";

import { useDynamicFormsTranslate } from "@ryde/redux";
import {
  checkCleanupNestedValidation,
  Log,
  noop,
  TimeZoneEnum,
  timeZoneList,
  ValidationHelpers,
  type ICustomField,
  type ISelectListItem,
} from "@ryde/shared";

import { FullWidthTextFieldWrapper } from "../parts-styled";
import { FieldExplanationIcon } from "./_parts";
import {
  DropdownOptionRenderer,
  getDropdownValuesLabels,
  ListBox,
  useInfluencedDropdownTemplate,
} from "./dropdowns";

interface IDropdownTemplateProps extends ICustomField {
  size?: "small" | "medium";
  formValueToDropdownValue?: (val: unknown) => unknown;
  dropdownValueToFormValue?: (val: unknown) => unknown;
  onBlur?: (formOnBlur: () => void) => void;
}

export const TimeZoneDropdownTemplate: FC<IDropdownTemplateProps> = ({
  id,
  name,
  formLabel,
  formAttributes,
  formValidation,
  size = "medium",
  formValueToDropdownValue,
  dropdownValueToFormValue,
  e2eData = "timezone-dropdown-template",
  onBlur,
}) => {
  const [selectItems, setSelectItems] = useState<ISelectListItem[] | undefined>(
    undefined,
  );

  useEffect(() => {
    if (timeZoneList?.length && !selectItems) {
      setSelectItems(timeZoneList);
    }
  }, [selectItems]);

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
  });
  const { disabled, boldValues, disabledValues } = formAttributes || {};

  useInfluencedDropdownTemplate({ formAttributes, name });

  const [options, mapOptionLabel] = useMemo(() => {
    return getDropdownValuesLabels({
      dropDownItems: selectItems,
      transformLabelFn: undefined,
    });
  }, [selectItems]);

  const getOptionLabel = useCallback(
    (value: unknown) => String(mapOptionLabel.get(value as string | number)),
    [mapOptionLabel],
  );

  const requiredComputed = useMemo(
    () => ValidationHelpers.hasRequiredRule(formValidation),
    [formValidation],
  );

  const onChangeDropdown = useEvent(
    (
      e: SyntheticEvent<Element, Event>,
      newValue: string | number | boolean,
    ): void => {
      const valueNormalized = dropdownValueToFormValue
        ? dropdownValueToFormValue(newValue)
        : newValue;
      Log.debug(
        "timezone Dropdown template value changed to:",
        valueNormalized,
      );
      setValue(name, valueNormalized, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
  );

  return (
    <FullWidthTextFieldWrapper>
      <Controller
        control={control}
        name={name}
        render={({
          field: { value: formValue, onChange: _onChange, ...restField },
          fieldState: { error },
        }) => {
          const valueNormalized = formValueToDropdownValue
            ? formValueToDropdownValue(formValue)
            : formValue;
          return (
            <FormControl
              data-e2e-template="timezone-dropdown-template"
              data-e2e-field-name={name}
              required={requiredComputed}
              fullWidth
              error={!!error}
            >
              <Autocomplete
                {...restField}
                data-e2e={e2eData}
                data-e2e-id={`${name}_${id || ""}`}
                options={options || []}
                renderOption={(props, option) =>
                  DropdownOptionRenderer({
                    listItemProps: props,
                    option: option as string | number,
                    getOptionLabel,
                    boldValues,
                    disabledValues,
                  })
                }
                value={valueNormalized || TimeZoneEnum.AsiaJerusalem}
                slotProps={{
                  listbox: {
                    component: ListBox,
                  },
                }}
                size={size}
                disabled={disabled}
                onChange={(event, data) => {
                  checkCleanupNestedValidation(name, errors);
                  onChangeDropdown(event, data as string | boolean | number);
                }}
                onBlur={() => {
                  if (onBlur) {
                    onBlur(restField?.onBlur ?? noop);
                  } else {
                    restField?.onBlur?.();
                  }
                }}
                isOptionEqualToValue={(opt, val) => String(opt) === String(val)}
                getOptionLabel={getOptionLabel}
                renderInput={params => (
                  <TextField
                    {...params}
                    disabled={disabled}
                    error={!!error}
                    label={formLabelTranslated}
                    required={requiredComputed}
                    data-e2e="timezone-dropdown-template-input"
                  />
                )}
              />
              {error && (
                <FormHelperText data-e2e="timezone-dropdown-template-error">
                  {error.message}
                </FormHelperText>
              )}
            </FormControl>
          );
        }}
      />
      <FieldExplanationIcon formAttributes={formAttributes} />
    </FullWidthTextFieldWrapper>
  );
};
